import { QuestionIcon, SettingsIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  List,
  ListItem,
  Popover,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Td,
  Text,
  Textarea,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Message, Response, Run, Session, User } from "core";
import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  Firestore,
  getDocs,
  limit,
  orderBy,
  Query,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import humanizeString from "humanize-string";
import React, { ComponentProps, useState } from "react";
import { Outlet, useLocation, useParams, useNavigate } from "react-router-dom";
import { ToolCall } from "core";
import {
  useFirestore,
  useFirestoreCollectionData,
  useFirestoreDocData,
  useFunctions,
} from "reactfire";
import { httpsCallable } from "firebase/functions";
import TableCard from "../../../components/TableCard";
import { WithId } from "../../../types/utils";
import ResponseRow, { columnNames as responseColumnNames } from "./ResponseRow";
import RunRow, { columnNames as runColumnNames } from "./RunRow";
import UserRow, { columnNames as userColumnNames } from "./UserRow";
import CommunitySettingsModal from "./CommunitySettingsModal";
import Announcements from "../Announcements";
import ToolCallModal from "../../../components/ToolCallModal";

type Mode = "all" | "pending";
const modes = ["all", "pending", "finalized"] as Mode[];

type T = ComponentProps<typeof Popover>;

export default function CommunityIndex() {
  let location = useLocation();
  let { id } = useParams();

  const isRoot = location.pathname.endsWith(id!);

  if (isRoot) return <Community />;
  return <Outlet />;
}

function Community() {
  let { id } = useParams();

  const firestore = useFirestore();
  const document = doc(collection(firestore, "communities"), id);
  const { data, status } = useFirestoreDocData(document);
  const [mode, setMode] = useState<Mode>("all");
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();

  const runsQuery = query(
    collection(firestore, "runs"),
    where("communityId", "==", id)
  ) as Query<Run>;
  const { data: runs = [] } = useFirestoreCollectionData(
    runsQuery as Query<WithId<Run>>,
    { idField: "id" }
  );
  
  const responsesQuery = getResponsesQuery(firestore, id!, mode);
  const { data: responses = [] } = useFirestoreCollectionData(
    responsesQuery as Query<WithId<Response>>,
    { idField: "id" }
  );

  // Fetch users and their session counts
  const [users, setUsers] = React.useState<any[]>([]);
  const [usersLoading, setUsersLoading] = React.useState(true);
  const [sessionCounts, setSessionCounts] = React.useState<Record<string, number>>({});
  
  // Three-column layout state
  const [selectedUser, setSelectedUser] = React.useState<any | null>(null);
  const [selectedSession, setSelectedSession] = React.useState<(Session & { id: string }) | null>(null);
  const [sessions, setSessions] = React.useState<(Session & { id: string })[]>([]);
  const [sessionsLoading, setSessionsLoading] = React.useState(false);
  const [messages, setMessages] = React.useState<(Message & { id: string; isAdminMessage?: boolean })[]>([]);
  const [messagesLoading, setMessagesLoading] = React.useState(false);
  const [adminMessage, setAdminMessage] = React.useState("");
  const [sendingMessage, setSendingMessage] = React.useState(false);
  const [selectedToolCall, setSelectedToolCall] = React.useState<ToolCall | null>(null);
  const toast = useToast();
  const functions = useFunctions();
  const { isOpen: isToolCallModalOpen, onOpen: onToolCallModalOpen, onClose: onToolCallModalClose } = useDisclosure();

  React.useEffect(() => {
    async function fetchUsersAndSessions() {
      try {
        setUsersLoading(true);

        // Fetch all users in the community
        const usersQuery = query(
          collection(firestore, "users"),
          where("communityId", "==", id),
          orderBy("createdAt", "desc")
        );
        const usersSnapshot = await getDocs(usersQuery);

        const allUsers = usersSnapshot.docs.map((doc) => ({
          ...doc.data(),
          uid: doc.id,
        }));

        setUsers(allUsers);

        // Fetch session counts for each user
        const counts: Record<string, number> = {};

        await Promise.all(
          allUsers.map(async (user) => {
            try {
              const sessionsQuery = query(
                collection(firestore, "users", user.uid, "sessions"),
                where("communityId", "==", id)
              );
              const sessionsSnapshot = await getDocs(sessionsQuery);
              counts[user.uid] = sessionsSnapshot.size;
            } catch (error) {
              console.error(`Error fetching sessions for user ${user.uid}:`, error);
              counts[user.uid] = 0;
            }
          })
        );

        setSessionCounts(counts);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setUsersLoading(false);
      }
    }

    if (id) {
      fetchUsersAndSessions();
    }
  }, [id, firestore]);

  // Fetch sessions when a user is selected
  React.useEffect(() => {
    async function fetchSessions() {
      if (!selectedUser || !id) return;

      try {
        setSessionsLoading(true);
        setSessions([]);
        setSelectedSession(null);
        setMessages([]);

        const sessionsQuery = query(
          collection(firestore, "users", selectedUser.uid, "sessions"),
          where("communityId", "==", id),
          orderBy("createdAt", "desc")
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);

        const fetchedSessions = sessionsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as (Session & { id: string })[];

        setSessions(fetchedSessions);

        // Auto-select the first session
        if (fetchedSessions.length > 0) {
          setSelectedSession(fetchedSessions[0]);
        }
      } catch (error) {
        console.error("Error fetching sessions:", error);
      } finally {
        setSessionsLoading(false);
      }
    }

    fetchSessions();
  }, [selectedUser, id, firestore]);

  // Fetch messages when a session is selected
  React.useEffect(() => {
    async function fetchMessages() {
      if (!selectedUser || !selectedSession) return;

      try {
        setMessagesLoading(true);

        const messagesQuery = query(
          collection(
            firestore,
            "users",
            selectedUser.uid,
            "sessions",
            selectedSession.id,
            "messages"
          ),
          orderBy("createdAt", "asc")
        );
        const messagesSnapshot = await getDocs(messagesQuery);

        const fetchedMessages = messagesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as (Message & { id: string; isAdminMessage?: boolean })[];

        setMessages(fetchedMessages);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setMessagesLoading(false);
      }
    }

    fetchMessages();
  }, [selectedSession, selectedUser, firestore]);

  const sendAdminMessage = async () => {
    if (!adminMessage.trim() || !selectedUser?.uid || !id) return;

    try {
      setSendingMessage(true);
      const sendAdminMessageFn = httpsCallable(functions, "sendAdminMessage");
      
      await sendAdminMessageFn({
        userId: selectedUser.uid,
        message: adminMessage.trim(),
        communityId: id,
      });

      toast({
        title: "Message sent successfully",
        description: "The WhatsApp message has been sent to the user.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setAdminMessage("");
      
      // Refresh messages to show the new admin message
      if (selectedSession) {
        const messagesQuery = query(
          collection(
            firestore,
            "users",
            selectedUser.uid,
            "sessions",
            selectedSession.id,
            "messages"
          ),
          orderBy("createdAt", "asc")
        );
        const messagesSnapshot = await getDocs(messagesQuery);
        const fetchedMessages = messagesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as (Message & { id: string; isAdminMessage?: boolean })[];
        setMessages(fetchedMessages);
      }
    } catch (error) {
      console.error("Error sending admin message:", error);
      toast({
        title: "Failed to send message",
        description: error instanceof Error ? error.message : "An error occurred while sending the message.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingMessage(false);
    }
  };

  function handleApproveButtonClick(runId: string) {
    const run = doc(collection(firestore, "runs"), runId);
    updateDoc(run, { approvedAt: Timestamp.now() });
  }

  function onCreateRunButtonPress() {
    addDoc(collection(firestore, "runs"), {
      batchSize: 6,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      communityId: id,
    });
  }

  if (status === "loading") return <div>Loading...</div>;

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box fontSize="xl" fontWeight="bold">{data.name}</Box>
        <Button
          leftIcon={<SettingsIcon />}
          colorScheme="blue"
          onClick={onSettingsOpen}
        >
          Community Settings
        </Button>
      </Box>

      <CommunitySettingsModal
        isOpen={isSettingsOpen}
        onClose={onSettingsClose}
        communityId={id!}
        phoneNumber={data.phoneNumber}
      />

      <Tabs>
        <TabList>
          <Tab>Runs</Tab>
          <Tab>Users</Tab>
          <Tab>Announcements</Tab>
          <Tab>Responses (Legacy)</Tab>
        </TabList>

        <TabPanels>
          {/* Runs Tab */}
          <TabPanel>
            <TableCard
              columnNames={runColumnNames}
              title="Matching Runs"
              action={
                <Box display="flex" justifyContent="flex-end">
                  <Tooltip label="Clicking this button will run the matching with OpenAI. It costs money, and runs every hour. After approving the run, check back later :)">
                    <IconButton
                      variant="ghost"
                      aria-label="info"
                      icon={<QuestionIcon />}
                      mr={2}
                    />
                  </Tooltip>

                  <Button onClick={onCreateRunButtonPress}>Create a run</Button>
                </Box>
              }
            >
              {runs.map((run) => (
                <RunRow
                  key={run.id}
                  run={run}
                  handleApproveButtonClick={() => handleApproveButtonClick(run.id)}
                />
              ))}
            </TableCard>
          </TabPanel>

          {/* Users Tab - Three Column Layout */}
          <TabPanel p={0}>
            <Flex gap={0} height="80vh" borderWidth={1} borderRadius="md" overflow="hidden">
              {/* Column 1: Users List */}
              <Box
                width="25%"
                borderRightWidth={1}
                overflowY="auto"
                bg="gray.50"
              >
                <Box p={4} bg="white" borderBottomWidth={1} position="sticky" top={0} zIndex={1}>
                  <Heading size="md">Users ({users.length})</Heading>
                </Box>
                {usersLoading ? (
                  <Flex justify="center" align="center" height="200px">
                    <Spinner />
                  </Flex>
                ) : users.length === 0 ? (
                  <Box p={4}>
                    <Text color="gray.500">No users found</Text>
                  </Box>
                ) : (
                  <List spacing={0}>
                    {users.map((user) => {
                      const isActive = selectedUser?.uid === user.uid;
                      const sessionCount = sessionCounts[user.uid] || 0;
                      
                      return (
                        <ListItem
                          key={user.uid}
                          p={4}
                          cursor="pointer"
                          bg={isActive ? "blue.50" : "white"}
                          borderBottomWidth={1}
                          borderLeftWidth={4}
                          borderLeftColor={isActive ? "blue.500" : "transparent"}
                          _hover={{ bg: isActive ? "blue.50" : "gray.100" }}
                          onClick={() => setSelectedUser(user)}
                        >
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="semibold" fontSize={14}>
                              {user.name}
                            </Text>
                            <Text fontSize={12} color="gray.600">
                              {user.phoneNumber}
                            </Text>
                            <Badge colorScheme="purple" fontSize={10}>
                              {sessionCount} session{sessionCount !== 1 ? 's' : ''}
                            </Badge>
                          </VStack>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>

              {/* Column 2: Sessions List */}
              <Box
                width="30%"
                borderRightWidth={1}
                overflowY="auto"
                bg="gray.50"
              >
                <Box p={4} bg="white" borderBottomWidth={1} position="sticky" top={0} zIndex={1}>
                  <Heading size="md">Sessions ({sessions.length})</Heading>
                  {selectedUser && (
                    <Text fontSize={12} color="gray.600" mt={1}>
                      {selectedUser.name}
                    </Text>
                  )}
                </Box>
                {!selectedUser ? (
                  <Flex justify="center" align="center" height="200px" p={4}>
                    <Text color="gray.500" textAlign="center">
                      Select a user to view their sessions
                    </Text>
                  </Flex>
                ) : sessionsLoading ? (
                  <Flex justify="center" align="center" height="200px">
                    <Spinner />
                  </Flex>
                ) : sessions.length === 0 ? (
                  <Box p={4}>
                    <Text color="gray.500">No sessions found</Text>
                  </Box>
                ) : (
                  <List spacing={0}>
                    {sessions.map((session) => {
                      const isActive = selectedSession?.id === session.id;
                      const isExpired = session.expiresAt
                        ? (session.expiresAt as Timestamp).toDate() < new Date()
                        : false;
                      const isAnalyzed = !!session.analyzedAt;

                      return (
                        <ListItem
                          key={session.id}
                          p={4}
                          cursor="pointer"
                          bg={isActive ? "blue.50" : "white"}
                          borderBottomWidth={1}
                          borderLeftWidth={4}
                          borderLeftColor={isActive ? "blue.500" : "transparent"}
                          _hover={{ bg: isActive ? "blue.50" : "gray.100" }}
                          onClick={() => setSelectedSession(session)}
                        >
                          <VStack align="start" spacing={1}>
                            <HStack justify="space-between" width="100%">
                              <Text fontWeight="semibold" fontSize={13}>
                                {session.id.substring(0, 12)}...
                              </Text>
                              {isExpired && (
                                <Badge colorScheme={isAnalyzed ? "green" : "yellow"} fontSize={10}>
                                  {isAnalyzed ? "Analyzed" : "Pending"}
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize={12} color="gray.600">
                              Created: {(session.createdAt as Timestamp)?.toDate().toLocaleDateString()}
                            </Text>
                            <Text fontSize={12} color="gray.600">
                              Expires: {(session.expiresAt as Timestamp)?.toDate().toLocaleDateString()}
                            </Text>
                          </VStack>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>

              {/* Column 3: Messages */}
              <Box
                width="45%"
                overflowY="auto"
                bg="white"
              >
                <Box p={4} bg="white" borderBottomWidth={1} position="sticky" top={0} zIndex={1}>
                  <Heading size="md">Messages ({messages.length})</Heading>
                  {selectedSession && (
                    <Text fontSize={12} color="gray.600" mt={1}>
                      Session: {selectedSession.id.substring(0, 12)}...
                    </Text>
                  )}
                </Box>
                {!selectedSession ? (
                  <Flex justify="center" align="center" height="200px" p={4}>
                    <Text color="gray.500" textAlign="center">
                      Select a session to view messages
                    </Text>
                  </Flex>
                ) : messagesLoading ? (
                  <Flex justify="center" align="center" height="200px">
                    <Spinner />
                  </Flex>
                ) : messages.length === 0 ? (
                  <Box p={4}>
                    <Text color="gray.500">No messages in this session</Text>
                  </Box>
                ) : (
                  <VStack align="stretch" spacing={3} p={4}>
                    {messages.map((message) => {
                      const isUser = message.role === "user";
                      const hasToolCall = 
                        (message.role === "assistant" || message.role === "tool") && 
                        "toolCall" in message && 
                        message.toolCall;
                      
                      return (
                        <Box
                          key={message.id}
                          p={3}
                          borderRadius="md"
                          bg={isUser ? "blue.50" : "gray.50"}
                          borderLeftWidth={4}
                          borderLeftColor={isUser ? "blue.500" : "gray.500"}
                        >
                          <HStack justify="space-between" mb={2}>
                            <HStack>
                              <Badge colorScheme={isUser ? "blue" : "gray"} fontSize={10}>
                                {message.role}
                              </Badge>
                              {message.isAdminMessage && (
                                <Badge colorScheme="purple" fontSize={9}>
                                  Admin
                                </Badge>
                              )}
                              {hasToolCall && "toolCall" in message && message.toolCall && (
                                <Badge
                                  colorScheme="orange"
                                  fontSize={9}
                                  cursor="pointer"
                                  onClick={() => {
                                    if ("toolCall" in message && message.toolCall) {
                                      setSelectedToolCall(message.toolCall);
                                      onToolCallModalOpen();
                                    }
                                  }}
                                  _hover={{ transform: "scale(1.05)" }}
                                  transition="transform 0.1s"
                                >
                                  🔧 {message.toolCall.name}
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize={11} color="gray.500">
                              {(message.createdAt as Timestamp)?.toDate().toLocaleString()}
                            </Text>
                          </HStack>
                          <Text whiteSpace="pre-wrap" fontSize={13}>
                            {message.message}
                          </Text>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
                
                {/* Admin Message Input */}
                {selectedUser && (
                  <>
                    <Divider my={4} />
                    <Box p={4}>
                      <Heading size="sm" mb={3} color="purple.600">
                        Send Admin Message
                      </Heading>
                      {!selectedUser.phoneNumber ? (
                        <Alert status="warning" borderRadius="md">
                          <AlertIcon />
                          <Text fontSize="sm">
                            User has no phone number. Cannot send WhatsApp message.
                          </Text>
                        </Alert>
                      ) : (
                        <VStack align="stretch" spacing={3}>
                          <Textarea
                            placeholder="Type your message here..."
                            value={adminMessage}
                            onChange={(e) => setAdminMessage(e.target.value)}
                            rows={3}
                            resize="vertical"
                            fontSize={13}
                          />
                          <HStack justify="space-between">
                            <Text fontSize={11} color="gray.500">
                              This message will be sent via WhatsApp to {selectedUser.phoneNumber}
                            </Text>
                            <Button
                              colorScheme="purple"
                              size="sm"
                              onClick={sendAdminMessage}
                              isLoading={sendingMessage}
                              loadingText="Sending..."
                              isDisabled={!adminMessage.trim()}
                              fontSize={12}
                            >
                              Send Message
                            </Button>
                          </HStack>
                        </VStack>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Flex>
          </TabPanel>

          {/* Announcements Tab */}
          <TabPanel>
            <Announcements />
          </TabPanel>

          {/* Responses Tab */}
          <TabPanel>
            <TableCard
              columnNames={responseColumnNames}
              title="User Responses"
              controls={
                <RadioGroup
                  mb={4}
                  onChange={(m) => setMode(m as typeof mode)}
                  value={mode}
                >
                  <Stack direction="row">
                    {modes.map((m) => (
                      <Radio key={m} value={m}>
                        {humanizeString(m)}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              }
            >
              {responses.map((response) => (
                <ResponseRow key={response.id} response={response} />
              ))}
            </TableCard>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Tool Call Modal */}
      {selectedToolCall && (
        <ToolCallModal
          isOpen={isToolCallModalOpen}
          onClose={onToolCallModalClose}
          toolCall={selectedToolCall}
        />
      )}
    </div>
  );
}

function getResponsesQuery(
  firestore: Firestore,
  communityId: string,
  mode: Mode
) {
  if (mode === "pending") {
    return query(
      collection(firestore, "responses"),
      where("communityId", "==", communityId),
      where("finalizedAt", "==", null)
    );
  } else if (mode === "all") {
    return query(
      collection(firestore, "responses"),
      where("communityId", "==", communityId)
    ) as Query<Run>;
  } else if (mode === "finalized") {
    return query(
      collection(firestore, "responses"),
      where("communityId", "==", communityId),
      where("finalizedAt", "!=", null)
    ) as Query<Run>;
  }
}
